import { GameAction, GameState, CardColor, CardType, Player } from '../types'
import * as gameLogic from './gameLogic'
import { GamePhase } from '../types'
import { toggleDiscardMode, discardCardForEssence, discardStashCardForEssence, discardPreparedColorCardForEssence } from './discard'
import { STASH_DECK_COMPOSITION, DECK_COMPOSITION, getCardDisplayName } from '../cardCatalog'
import { Language } from '../data/translations'

const validate = (state: GameState, action: GameAction): boolean => {
  if (!state) return false
  const p = state.players.find(x => x.id === (action as any).playerId)
  if (!p) return false
  switch (action.type) {
    case 'CHOOSE_TOWER':
      return state.phase === GamePhase.TowerChoice && state.availableTowers.includes(action.towerColor)
    case 'CHOOSE_STASH':
      return (
        state.phase === GamePhase.StashChoice &&
        (state.stashChoicePicksRemaining?.[action.playerId] ?? 0) > 0 &&
        !!state.stashChoiceOptions[action.playerId]?.some(c => c.id === action.cardId)
      )
    case 'PREPARE_CARD':
      return state.phase === GamePhase.Preparation && p.hand.some(c => c.id === action.cardId)
    case 'RETURN_CARD':
      return state.phase === GamePhase.Preparation && p.prepared.some(pc => pc.card.id === action.cardId)
    case 'END_DAY':
      return state.phase === GamePhase.Preparation
    case 'ATTEMPT_ACTIVATION':
      return state.phase === GamePhase.Combat && state.turnPlayerId === action.playerId && p.prepared.some(pc => pc.card.id === action.cardId)
    case 'ATTEMPT_SHIELD_ACTIVATION':
      return state.phase === GamePhase.Combat && state.activations.length > 0 && p.prepared.some(pc => pc.card.id === action.cardId)
    case 'COUNTER_ACTIVATION':
      return state.phase === GamePhase.Combat && state.activations.length > 0 && p.prepared.some(pc => pc.card.id === action.cardId)
    case 'ACCEPT_ACTIVATION':
      return state.phase === GamePhase.Combat && state.activations.length > 0
    case 'PASS_TURN':
      return state.phase === GamePhase.Combat && state.turnPlayerId === action.playerId
    case 'CHOOSE_CHROMABOW_COLOR':
      return state.phase === GamePhase.AWAITING_CHROMABOW_CHOICE && state.chromabowInitiatorId === action.playerId
    case 'CHOOSE_HIBERNATION_CHOICE':
      return state.phase === GamePhase.AWAITING_HIBERNATION_CHOICE && state.hibernationInitiatorId === action.playerId
    case 'CHOOSE_HERO_CLASS':
      return state.phase === GamePhase.TowerChoice
    case 'CHOOSE_LORIAN_CLASS':
      return state.phase === GamePhase.LorianClassChoice && p.hero === 'LORIAN'
    case 'MAGE_CAST_SPELL': {
      const spellCard = p.hand.find(c => c.id === action.cardId && !!c.essenceCost && c.subType === 'Spell')
      if (!spellCard) return false
      // Check essence requirement
      return state.phase === GamePhase.Combat && p.essence >= spellCard.essenceCost
    }
    case 'ASTRID_BLAST':
      return (state.phase === GamePhase.Combat || state.phase === GamePhase.TowerPurchase)
    case 'CHOOSE_BURGLARY_CARD':
      return state.phase === GamePhase.AWAITING_BURGLARY_CHOICE && state.burglaryInitiatorId === action.playerId
    case 'CHOOSE_PURIFICATION_TARGET':
      return state.phase === GamePhase.AWAITING_PURIFICATION_TARGET && state.purificationInitiatorId === action.playerId
    case 'ASSIGN_VICTORY_POINT':
      return state.phase === GamePhase.GameOver && !!state.pendingVictory && state.pendingVictory.winnerId === action.playerId
    case 'COMPLETE_BROKEN_SECRET':
      return state.phase === GamePhase.BROKEN_SECRET_REVEAL && (state.brokenSecretState?.initiatorId === action.playerId)
    case 'RETURN_LOCKED_CARD_FROM_OVERFLOW':
      return state.phase === GamePhase.LockedCardOverflow && state.overflowPlayerId === action.playerId && p.prepared.some(pc => pc.card.id === action.cardId)
    case 'START_TOWER_PURCHASE':
      return state.phase === GamePhase.Combat && state.turnPlayerId === action.playerId && !p.capturedTowerThisTurn
    case 'CANCEL_TOWER_PURCHASE':
      return state.phase === GamePhase.TowerPurchase && state.turnPlayerId === action.playerId
    case 'PURCHASE_TOWER':
      return state.phase === GamePhase.TowerPurchase && state.turnPlayerId === action.playerId && !p.capturedTowerThisTurn
    case 'CHANGE_TOWER_COLOR':
      return (
        (state.phase === GamePhase.Combat || state.phase === GamePhase.TowerPurchase) &&
        state.turnPlayerId === action.playerId &&
        !!p.towers[action.towerIndex] &&
        p.hand.some(c => c.color === action.newColor && !c.essenceCost)
      )
    case 'TOGGLE_DISCARD_MODE':
      return state.phase === GamePhase.Preparation || state.phase === GamePhase.Combat || state.phase === GamePhase.TowerPurchase
    case 'DISCARD_CARD':
      return state.phase === GamePhase.Preparation || state.phase === GamePhase.Combat || state.phase === GamePhase.TowerPurchase
    case 'ROTATE_PREPARED_CARD':
      return state.phase === GamePhase.Preparation && p.prepared.some(pc => pc.card.id === action.cardId)
    case 'DEV_ADD_CARD':
      return true // Dev tool, always allowed
    default:
      return false
  }
}

export const applyGameAction = (state: GameState, action: GameAction): GameState | null => {
  try {
    if (!validate(state, action)) return null
    switch (action.type) {
      case 'CHOOSE_TOWER':
        return gameLogic.chooseTower(state, action.playerId, action.towerColor)
      case 'CHOOSE_STASH':
        return gameLogic.makeStashChoice(state, action.playerId, action.cardId, action.fromBurned)
      case 'PREPARE_CARD':
        return gameLogic.prepareCard(state, action.playerId, action.cardId, action.rotation)
      case 'RETURN_CARD':
        return gameLogic.returnCardToHand(state, action.playerId, action.cardId)
      case 'END_DAY':
        return gameLogic.startCombat(state)
      case 'ATTEMPT_ACTIVATION':
        return gameLogic.attemptActivation(state, action.playerId, action.cardId, action.rotation, action.targetId)
      case 'ATTEMPT_SHIELD_ACTIVATION':
        return gameLogic.attemptShieldActivation(state, action.playerId, action.cardId, action.rotation, action.targetActivationId)
      case 'COUNTER_ACTIVATION':
        return gameLogic.counterActivation(state, action.playerId, action.cardId, action.rotation, action.targetActivationId)
      case 'ACCEPT_ACTIVATION':
        return gameLogic.acceptActivation(state, action.playerId)
      case 'PASS_TURN':
        return gameLogic.passTurn(state, action.playerId)
      case 'CHOOSE_CHROMABOW_COLOR':
        return gameLogic.chooseChromabowColor(state, action.playerId, action.color)
      case 'CHOOSE_HIBERNATION_CHOICE':
        return gameLogic.chooseHibernationColor(state, action.playerId, action.color)
      case 'CHOOSE_BURGLARY_CARD':
        return gameLogic.chooseBurglaryCard(state, action.playerId, action.cardId)
      case 'CHOOSE_PURIFICATION_TARGET':
        return gameLogic.choosePurificationTarget(state, action.targetColor)
      case 'CHOOSE_HERO_CLASS':
        return gameLogic.chooseHeroClass(state, action.playerId, action.hero, action.playerClass)
      case 'CHOOSE_LORIAN_CLASS':
        return gameLogic.chooseLorianClass(state, action.playerId, action.playerClass)
      case 'MAGE_CAST_SPELL':
        return gameLogic.mageCastSpellFromHand(state, action.playerId, action.cardId, action.rotation, action.targetId, action.targetActivationId)
      case 'ASTRID_BLAST':
        return gameLogic.astridHeroBlast(state, action.playerId, action.targetId, action.color)
      case 'ASSIGN_VICTORY_POINT':
        return gameLogic.assignVictoryPoint(state, action.playerId, action.zoneId, action.factionId, action.basePoints)
      case 'COMPLETE_BROKEN_SECRET':
        return gameLogic.completeBrokenSecret(state)
      case 'RETURN_LOCKED_CARD_FROM_OVERFLOW':
        return gameLogic.returnLockedCardFromOverflow(state, action.cardId)
      case 'START_TOWER_PURCHASE':
        return gameLogic.startTowerPurchase(state)
      case 'CANCEL_TOWER_PURCHASE':
        return gameLogic.cancelTowerPurchase(state)
      case 'PURCHASE_TOWER':
        return gameLogic.purchaseTower(state, action.playerId, action.towerColor, action.preferOpponent)
      case 'CHANGE_TOWER_COLOR':
        return gameLogic.changeTowerColor(
          gameLogic.openTowerChangeMenu(state, action.playerId, action.towerIndex),
          action.newColor
        )
      case 'TOGGLE_DISCARD_MODE':
        return toggleDiscardMode(state, action.playerId)
      case 'ROTATE_PREPARED_CARD':
        return gameLogic.rotatePreparedCard(state, action.playerId, action.cardId, action.rotation)
      case 'DISCARD_CARD': {
        if (action.discardType === 'day') {
          return discardCardForEssence(state, action.playerId, action.cardId)
        } else if (action.discardType === 'stash') {
          return discardStashCardForEssence(state, action.playerId, action.cardId)
        } else if (action.discardType === 'prepared_color') {
          return discardPreparedColorCardForEssence(state, action.playerId, action.cardId)
        }
        return null
      }
      case 'DEV_ADD_CARD': {
        const nameNorm = action.cardName.trim().toLowerCase()
        const langs: Language[] = ['en', 'cn', 'uk', 'ru']

        // 1. Match Stash Deck (by Slug or any translated Name)
        const stashProto = STASH_DECK_COMPOSITION.find(ci => {
          const slug = (ci as any).faceSlug?.toLowerCase()
          if (slug === nameNorm) return true
          return langs.some(l => getCardDisplayName(ci.type, ci.value, ci.subType, (ci as any).faceSlug, l).toLowerCase() === nameNorm)
        })

        // 2. Match Color Deck (by Type or any translated Name)
        const colorProto = DECK_COMPOSITION.find(ci => {
          const typeStr = String(ci.type).toLowerCase()
          if (typeStr === nameNorm) return true
          return langs.some(l => getCardDisplayName(ci.type, ci.value, undefined, undefined, l).toLowerCase() === nameNorm)
        })

        const ownerId = action.targetPlayerId
        const getColor = (type: CardType): CardColor => {
          switch (type) {
            case CardType.Attack: case CardType.EnergySword: case CardType.FlyingScythe:
            case CardType.InfernoGauntlet: case CardType.PlantedBomb: case CardType.Chromabow:
            case CardType.LifeDrain: case CardType.Trembling: return CardColor.Red
            case CardType.Defense: case CardType.FortunesFavor: case CardType.CursedIron:
            case CardType.ArcaneBoots: case CardType.Ale: case CardType.Protector:
            case CardType.ThunderSprint: case CardType.SealOfSilence: return CardColor.Yellow
            case CardType.PsySearch: case CardType.BrokenSecret: return CardColor.Black
            case CardType.Steal: case CardType.StealTower: case CardType.EyesOfMadness:
            case CardType.MadnessControl: case CardType.Larcendagger: case CardType.TwilightShift: return CardColor.Purple
            case CardType.BodyUpgrade: case CardType.Counterblast: case CardType.Frost:
            case CardType.Invincible: return CardColor.Pink
            case CardType.LifeCharger: case CardType.NaturalGrip: return CardColor.Green
            default: return CardColor.Black
          }
        }
        const baseId = `dev-${state.seed}-${state.round}-${state.log.length}-${action.playerId}-${action.targetPlayerId}-${encodeURIComponent(nameNorm)}`
        let proto: any = null
        if (stashProto) {
          proto = { ...stashProto, id: `${baseId}-base`, color: getColor(stashProto.type), ownerId }
        } else if (colorProto) {
          proto = { ...colorProto, id: `${baseId}-base`, ownerId }
        }
        if (!proto) return null
        const cards = Array(Math.max(1, Math.min(10, action.count))).fill(0).map((_, index) => ({ ...proto, id: `${baseId}-${index}`, ownerId }))
        const players = state.players.map(p => p.id === ownerId ? { ...p, hand: [...p.hand, ...cards] } : p) as [Player, Player]
        return {
          ...state,
          players,
          log: [...state.log, `Dev: Added ${cards.length} ${getCardDisplayName(proto.type, proto.value, proto.subType, (proto as any).faceSlug)} card(s) to Player ${ownerId}.`]
        }
      }
      default:
        return null
    }
  } catch {
    return null
  }
}
